import useComponentDidUnmount from 'App/components/hooks/useComponentDidUnmount';
import { useSelector } from 'App/components/hooks';
import { CancelablePromise, makeCancelable } from 'Helpers/promise';
import { useTranslation } from 'Components/Helpers/translate';
import { Flex, Help, Toggle, Tooltip, Icon } from '@upsales/components';
import { MAX_CACHED_PRODUCTS } from 'Helpers/product';
import ClientParam from 'App/resources/ClientParam';
import AdminHeader from './AdminHeader';
import logError from 'Helpers/logError';
import React, { useRef } from 'react';

interface AdminProductHeaderProps {
	tabs?: Array<{ id: string; title: string }>;
	selectedTab?: 'products' | 'categories';
	onTabChange?: (tab: string) => void;
}

const AdminProductHeader: React.FC<AdminProductHeaderProps> = ({
	tabs,
	selectedTab,
	onTabChange
}) => {
	const { t } = useTranslation();
	const {
		totals: { products },
		metadata
	} = useSelector(({ App }) => App);

	const checked = metadata?.params.OrderProductsImprovedSearch;
	const useCategoryTabAsDefault = metadata?.params.EnableProductCategoryTabDefault;
	const showToggle = products <= MAX_CACHED_PRODUCTS;
	const toggleDisabled = products === 0;
	const clientParamReq = useRef<CancelablePromise | null>(null);
	const categoryDefaultParamReq = useRef<CancelablePromise | null>(null);

	useComponentDidUnmount(() => {
		clientParamReq.current?.cancel?.();
		categoryDefaultParamReq.current?.cancel?.();
	});

	const onToggleChange = (value: boolean) => {
		clientParamReq.current = makeCancelable(ClientParam.save({ id: 261, value }));
		clientParamReq.current.promise.catch(logError);
	};

	const onCategoryDefaultToggleChange = (value: boolean) => {
		categoryDefaultParamReq.current = makeCancelable(ClientParam.save({ id: 269, value }));
		categoryDefaultParamReq.current.promise.catch(logError);
	};

	return (
		<>
			<AdminHeader
				title={t('default.products')}
				description={
					<div>
						{t('admin.products.description')} <Help sidebar articleId={1442} />
					</div>
				}
				image="products.svg"
				tabs={tabs}
				selectedTab={selectedTab}
				onTabChange={onTabChange}
			/>
			<Flex direction="column" gap="8" space="mlxl ptm pbm">
				{showToggle ? (
					<Flex alignItems="center" gap="8">
						<Toggle disabled={toggleDisabled} checked={checked} onChange={onToggleChange} />
						<label>
							{t('admin.products.header.toggleLabelImprovedSearch')}
							<Tooltip title={t('admin.products.header.toggleLabelImprovedSearchDesc')} position="right">
								<Icon name="question-circle" space="mlm" />
							</Tooltip>
						</label>
					</Flex>
				) : null}
				<Flex alignItems="center" gap="8">
					<Toggle checked={useCategoryTabAsDefault} onChange={onCategoryDefaultToggleChange} />
					<label>
						{t('admin.products.header.toggleLabelUseCategoryTabAsDefault')}
						<Tooltip
							title={t('admin.products.header.toggleLabelUseCategoryTabAsDefaultDesc')}
							position="right"
						>
							<Icon name="question-circle" space="mlm" />
						</Tooltip>
					</label>
				</Flex>
			</Flex>
		</>
	);
};

export default AdminProductHeader;
